import { useState, useEffect } from "react";
import { 
  HelpCircle, 
  Phone, 
  Building2, 
  Mail, 
  Clock, 
  CheckCircle2, 
  Trash2,
  MessageSquare,
  PhoneCall,
  AlertCircle,
  Filter
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "all", label: "Tutti" },
  { value: "pending", label: "In attesa", color: "text-yellow-400", bg: "bg-yellow-500/20" },
  { value: "in_progress", label: "In lavorazione", color: "text-blue-400", bg: "bg-blue-500/20" },
  { value: "resolved", label: "Risolto", color: "text-green-400", bg: "bg-green-500/20" }
];

const STATUS_MAP = {
  pending: { label: "In attesa", color: "text-yellow-400", bg: "bg-yellow-500/20", icon: AlertCircle },
  in_progress: { label: "In lavorazione", color: "text-blue-400", bg: "bg-blue-500/20", icon: Clock },
  resolved: { label: "Risolto", color: "text-green-400", bg: "bg-green-500/20", icon: CheckCircle2 }
};

export default function AdminSupportRequests({ user }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const url = filter === "all" 
        ? `${API}/admin/support-requests`
        : `${API}/admin/support-requests?status=${filter}`;
      const response = await authFetch(url);
      if (response.ok) {
        setRequests(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const response = await authFetch(`${API}/admin/support-requests/${requestId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success("Stato aggiornato");
        fetchRequests();
        if (selectedRequest?.request_id === requestId) {
          setSelectedRequest(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa richiesta?")) return;
    
    try {
      const response = await authFetch(`${API}/admin/support-requests/${requestId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Richiesta eliminata");
        setShowDialog(false);
        fetchRequests();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openRequestDetail = (request) => {
    setSelectedRequest(request);
    setShowDialog(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-support-requests-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <HelpCircle className="w-8 h-8 text-primary" />
          <div>
            <h1 className="page-title">Richieste Assistenza</h1>
            <p className="text-sm text-zinc-500">
              Gestisci le richieste di supporto degli utenti
            </p>
          </div>
        </div>
        
        {/* Filtri */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-500" />
          {STATUS_OPTIONS.map((status) => (
            <Button
              key={status.value}
              variant={filter === status.value ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(status.value)}
              className={filter === status.value ? "btn-primary" : "border-zinc-700 text-zinc-400"}
              data-testid={`filter-${status.value}`}
            >
              {status.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Statistiche rapide */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-400" />
            <div>
              <p className="text-2xl font-bold text-yellow-400">
                {requests.filter(r => r.status === "pending").length}
              </p>
              <p className="text-xs text-zinc-500 uppercase">In attesa</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-blue-400" />
            <div>
              <p className="text-2xl font-bold text-blue-400">
                {requests.filter(r => r.status === "in_progress").length}
              </p>
              <p className="text-xs text-zinc-500 uppercase">In lavorazione</p>
            </div>
          </div>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-400" />
            <div>
              <p className="text-2xl font-bold text-green-400">
                {requests.filter(r => r.status === "resolved").length}
              </p>
              <p className="text-xs text-zinc-500 uppercase">Risolte</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista Richieste */}
      {requests.length > 0 ? (
        <div className="space-y-3">
          {requests.map((request) => {
            const statusInfo = STATUS_MAP[request.status] || STATUS_MAP.pending;
            const StatusIcon = statusInfo.icon;
            return (
              <div 
                key={request.request_id} 
                className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 card-hover cursor-pointer"
                onClick={() => openRequestDetail(request)}
                data-testid={`request-${request.request_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${statusInfo.bg}`}>
                        <StatusIcon className={`w-4 h-4 ${statusInfo.color}`} />
                      </div>
                      <div>
                        <h3 className="font-bold text-white flex items-center gap-2">
                          {request.user_name || "Utente"}
                          <span className={`px-2 py-0.5 rounded text-xs uppercase ${statusInfo.bg} ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </h3>
                        <p className="text-xs text-zinc-500">
                          {request.user_type === "ricambista" ? "Ricambista" : "Officina"} • {request.company_name || "-"}
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-zinc-400 text-sm line-clamp-2 mb-3 pl-11">
                      {request.message}
                    </p>
                    
                    <div className="flex items-center gap-4 text-xs text-zinc-500 pl-11">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {request.email || "-"}
                      </span>
                      {request.wants_callback && (
                        <span className="flex items-center gap-1 text-primary">
                          <PhoneCall className="w-3 h-3" />
                          Richiesta callback: {request.callback_phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1 font-['JetBrains_Mono']">
                        <Clock className="w-3 h-3" />
                        {formatDate(request.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(request.request_id);
                    }}
                    className="text-zinc-400 hover:text-red-400"
                    data-testid={`delete-request-${request.request_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <HelpCircle className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">Nessuna richiesta</p>
          <p className="empty-state-text">
            {filter !== "all" ? "Nessuna richiesta con questo stato" : "Non ci sono richieste di assistenza"}
          </p>
        </div>
      )}

      {/* Dialog Dettaglio */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              Dettaglio Richiesta
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              {/* Info Utente */}
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Informazioni Utente</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Building2 className="w-4 h-4 text-zinc-500" />
                    <span>{selectedRequest.company_name || selectedRequest.user_name || "-"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Mail className="w-4 h-4 text-zinc-500" />
                    <span className="truncate">{selectedRequest.email || "-"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Phone className="w-4 h-4 text-zinc-500" />
                    <span>{selectedRequest.phone || "-"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <span className={`px-2 py-0.5 rounded text-xs uppercase ${
                      selectedRequest.user_type === "ricambista" 
                        ? "bg-purple-500/20 text-purple-400" 
                        : "bg-blue-500/20 text-blue-400"
                    }`}>
                      {selectedRequest.user_type === "ricambista" ? "Ricambista" : "Officina"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Messaggio */}
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Messaggio</p>
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                  <p className="text-zinc-300 whitespace-pre-wrap">{selectedRequest.message}</p>
                </div>
              </div>

              {/* Callback richiesto */}
              {selectedRequest.wants_callback && (
                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <PhoneCall className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-white">Richiesta di callback</p>
                      <p className="text-sm text-zinc-400">
                        Contattare al numero: <span className="text-primary font-bold">{selectedRequest.callback_phone}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Data */}
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Clock className="w-4 h-4" />
                <span>Ricevuto il {formatDate(selectedRequest.created_at)}</span>
              </div>

              {/* Azioni Stato */}
              <div className="border-t border-zinc-800 pt-4">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Cambia Stato</p>
                <div className="flex gap-2">
                  {["pending", "in_progress", "resolved"].map((status) => {
                    const info = STATUS_MAP[status];
                    const isActive = selectedRequest.status === status;
                    return (
                      <Button
                        key={status}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleStatusChange(selectedRequest.request_id, status)}
                        className={isActive ? `${info.bg} ${info.color} border-0` : "border-zinc-700 text-zinc-400"}
                        data-testid={`status-${status}`}
                      >
                        <info.icon className="w-4 h-4 mr-1" />
                        {info.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Azioni */}
              <div className="flex justify-between pt-4">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(selectedRequest.request_id)}
                  data-testid="delete-request-dialog"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>
                <Button 
                  onClick={() => setShowDialog(false)}
                  className="btn-primary"
                >
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
